import jsPDF from "jspdf";
import { PortfolioData } from "../types";
import {
  calculateYearsOfExperience,
  formatObjective,
} from "./experienceCalculator";

export const generateResumePDF = (data: PortfolioData): void => {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 10;
  const contentWidth = pageWidth - 2 * margin;
  let yPosition = margin;
  const descriptionFontSize = 10;

  // Colors - Modern professional palette
  const primaryColor = [41, 128, 185]; // Blue
  const accentColor = [52, 152, 219]; // Lighter blue
  const textColor = [33, 33, 33]; // Dark gray
  const lightGray = [200, 200, 200];
  const sectionBg = [245, 247, 250]; // Light gray background

  // Helper function to get accurate line height
  const getLineHeight = (fontSize: number): number => {
    return fontSize; // Standard line height ratio
  };

  // Helper function to add a new page if needed
  const checkPageBreak = (requiredHeight: number): boolean => {
    if (yPosition + requiredHeight > pageHeight - margin - 10) {
      doc.addPage();
      yPosition = margin;
      return true;
    }
    return false;
  };

  // Helper function to add text with word wrap and return actual height used
  const addText = (
    text: string,
    x: number,
    y: number,
    maxWidth: number,
    fontSize: number = 10,
    color: number[] = textColor,
    isBold: boolean = false,
    customLineHeight: number = 5
  ): number => {
    doc.setFontSize(fontSize);
    doc.setTextColor(color[0], color[1], color[2]);
    if (isBold) {
      doc.setFont("helvetica", "bold");
    } else {
      doc.setFont("helvetica", "normal");
    }

    const lines = doc.splitTextToSize(text, maxWidth);
    const lineHeight = customLineHeight || getLineHeight(fontSize);

    // Draw each line
    lines.forEach((line: string, index: number) => {
      doc.text(line, x, y + index * lineHeight);
    });

    // Return actual height: (number of lines - 1) * lineHeight + fontSize for the last line
    if (lines.length === 0) return 0;
    if (lines.length === 1) return fontSize * 0.5; // Single line uses just the font size
    return (lines.length - 1) * lineHeight + fontSize * 0.5;
  };

  // Helper function to draw a line
  const drawLine = (
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    color: number[] = lightGray,
    width: number = 0.5
  ) => {
    doc.setDrawColor(color[0], color[1], color[2]);
    doc.setLineWidth(width);
    doc.line(x1, y1, x2, y2);
  };

  // Helper function to add section header with better design
  const addSectionHeader = (title: string) => {
    checkPageBreak(20);
    yPosition += 8;

    // Section background
    doc.setFillColor(sectionBg[0], sectionBg[1], sectionBg[2]);
    doc.rect(margin - 5, yPosition - 8, contentWidth + 10, 12, "F");

    // Section title
    const titleHeight = addText(
      title.toUpperCase(),
      margin,
      yPosition,
      contentWidth,
      13,
      primaryColor,
      true
    );
    yPosition += titleHeight + 2;

    // Underline
    drawLine(
      margin,
      yPosition,
      pageWidth - margin,
      yPosition,
      primaryColor,
      1.5
    );
    yPosition += 6;
  };

  // ========== HEADER SECTION ==========
  const headerHeight = 55;
  doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
  doc.rect(0, 0, pageWidth, headerHeight, "F");

  // --- Name & Title ---
  yPosition = 18;
  addText(
    data.basics.name,
    margin,
    yPosition,
    contentWidth,
    24,
    [255, 255, 255],
    true
  );
  yPosition += 10;

  addText(
    `${data.basics.label} | Backend & Cloud Developer`,
    margin,
    yPosition,
    contentWidth,
    12,
    [230, 230, 230],
    true
  );
  yPosition += 8;

  // --- Contact Info ---
  const contactFontSize = 9;
  doc.setFont("helvetica", "normal");

  // Line 1 — Email & Phone
  addText(
    `${data.basics.email}   |   ${data.basics.phone}`,
    margin,
    yPosition,
    contentWidth,
    contactFontSize,
    [255, 255, 255]
  );
  yPosition += 5;

  // Line 2 — Website, LinkedIn, GitHub
  addText(
    "https://hardikbutani.in   |   https://linkedin.com/in/butani-hardik-11198a18b   |  https://github.com/hardikbutani222",
    margin,
    yPosition,
    contentWidth,
    contactFontSize,
    [255, 255, 255]
  );
  yPosition += 5;

  // Line 3 — Location
  addText(
    data.basics.location.address,
    margin,
    yPosition,
    contentWidth,
    contactFontSize,
    [255, 255, 255]
  );
  yPosition += 5;

  // --- Divider line ---
  drawLine(
    margin,
    headerHeight - 2,
    pageWidth - margin,
    headerHeight - 2,
    [255, 255, 255],
    0.3
  );

  // Reset for next content
  yPosition = headerHeight + 8;

  // ========== OBJECTIVE SECTION ==========
  addSectionHeader("Professional Summary");
  const objective = formatObjective(data.basics.objective);
  const objectiveHeight = addText(
    objective,
    margin,
    yPosition,
    contentWidth,
    10,
    textColor,
    false
  );
  yPosition += objectiveHeight + 5;

  // ========== EXPERIENCE SECTION ==========
  addSectionHeader("Professional Experience");

  data.experience.forEach((exp, index) => {
    checkPageBreak(10);
    // Job Title
    const titleHeight = addText(
      exp.title,
      margin,
      yPosition + 1,
      contentWidth,
      13,
      textColor,
      true
    );
    yPosition += titleHeight;

    // Company and Duration
    const companyInfo = `${exp.company} | ${exp.years}`;
    const companyHeight = addText(
      companyInfo,
      margin,
      yPosition,
      contentWidth,
      10,
      accentColor,
      false
    );
    yPosition += companyHeight;

    // Job Description - Bullet points with proper spacing
    exp.description.forEach((desc, descIndex) => {
      checkPageBreak(2);
      const bulletText = `• ${desc}`;
      const descHeight = addText(
        bulletText,
        margin + 3,
        yPosition,
        contentWidth - 8,
        descriptionFontSize,
        textColor,
        false
      );
      yPosition += descHeight + 1;
    });

    yPosition -= 2;

    // Divider line between experiences (except last)
    if (index < data.experience.length - 1) {
      drawLine(
        margin,
        yPosition,
        pageWidth - margin,
        yPosition,
        lightGray,
        0.3
      );
      yPosition += 5;
    }
  });

  // ========== SKILLS SECTION ==========
  addSectionHeader("Technical Skills");

  // Loop through all categories dynamically (no hardcoding)
  Object.entries(data.skills).forEach(([category, skillList]) => {
    if (!Array.isArray(skillList) || skillList.length === 0) return;
    checkPageBreak(10);
    // Category Title (Bold)
    const categoryHeight = addText(
      `${category}:`,
      margin,
      yPosition,
      contentWidth,
      11,
      textColor,
      true // bold
    );
    yPosition += categoryHeight;

    // Skills list as comma-separated text
    const skillText = skillList.join(", ");
    const skillsHeight = addText(
      skillText,
      margin + 4,
      yPosition,
      contentWidth - 10,
      descriptionFontSize,
      textColor,
      false
    );
    yPosition += skillsHeight + 1.5; // spacing between categories
  });

  // ========== EDUCATION SECTION ==========
  addSectionHeader("Education");

  data.education.forEach((edu, eduIndex) => {
    checkPageBreak(10);
    yPosition += 2;
    const degreeHeight = addText(
      edu.degree,
      margin,
      yPosition,
      contentWidth,
      11,
      textColor,
      true
    );
    yPosition += degreeHeight;

    const institutionHeight = addText(
      edu.institution,
      margin + 4,
      yPosition,
      contentWidth,
      descriptionFontSize,
      textColor,
      false
    );
    yPosition += institutionHeight;
    const yearsHeight = addText(
      edu.years,
      margin + 4,
      yPosition,
      contentWidth,
      descriptionFontSize,
      accentColor,
      false
    );
    yPosition += yearsHeight;

    if (eduIndex < data.education.length - 1) {
      drawLine(
        margin,
        yPosition,
        pageWidth - margin,
        yPosition,
        lightGray,
        0.3
      );
      yPosition += 5;
    }
  });

  // ========== PROJECTS SECTION ==========
  addSectionHeader("Key Projects");
  yPosition -= 1;

  data.projects
    .filter((project) => !project.hideInResume)
    .forEach((project, index) => {
      checkPageBreak(10);
      yPosition += 3;

      // ---- Project Name ----
      const projectNameHeight = addText(
        project.name,
        margin,
        yPosition,
        contentWidth,
        11,
        textColor,
        true
      );

      const projectNameWidth = doc.getTextWidth(project.name) + 2;

      // ---- URL next to project name ----
      if (project.url) {
        const urlText = `(${project.url})`;
        const urlFontSize = 9;
        const urlWidth = doc.getTextWidth(urlText);
        const availableSpace = contentWidth - projectNameWidth - 10;

        if (urlWidth < availableSpace) {
          addText(
            urlText,
            margin + projectNameWidth,
            yPosition,
            contentWidth - projectNameWidth,
            urlFontSize,
            accentColor
          );
          yPosition += projectNameHeight + 2;
        } else {
          yPosition += projectNameHeight;
          addText(
            urlText,
            margin,
            yPosition,
            contentWidth,
            urlFontSize,
            accentColor
          );
          yPosition += urlFontSize + 1;
        }
      } else {
        yPosition += projectNameHeight + 1;
      }
      yPosition -= 2;

      const descHeight = addText(
        project.description,
        margin + 4,
        yPosition,
        contentWidth - 4,
        10,
        textColor
      );
      yPosition += descHeight;

      // ---- Technologies (same line using addText) ----
      const techLabel = "Technologies: ";
      const techStack = project.technologies.join(", ");

      // (1) Draw label (bold)
      const labelHeight = addText(
        techLabel,
        margin + 4,
        yPosition,
        contentWidth,
        descriptionFontSize,
        textColor,
        true // bold
      );

      // Measure label width AFTER drawing it
      const labelWidth = doc.getTextWidth(techLabel);

      // Remaining width for technologies
      const remainingWidth = contentWidth - labelWidth - 4;

      // (2) Draw technologies on the SAME LINE — with wrapping support
      const techHeight = addText(
        techStack,
        margin + 4 + labelWidth,
        yPosition,
        remainingWidth,
        descriptionFontSize,
        textColor,
        false // normal text
      );

      // Move Y down only ONCE
      yPosition += Math.max(labelHeight, techHeight) + 2;

      // ---- Divider ----
      if (index < data.projects.length - 1) {
        yPosition -= 2;
        drawLine(
          margin,
          yPosition,
          pageWidth - margin,
          yPosition,
          lightGray,
          0.3
        );
        yPosition += 2;
      }
    });

  // ========== AWARDS SECTION ==========
  if (data.awards && data.awards.length > 0) {
    yPosition += 1;
    addSectionHeader("Awards & Recognition");

    data.awards.forEach((award, awardIndex) => {
      checkPageBreak(5);
      yPosition += 2;
      const awardNameHeight = addText(
        award.name,
        margin,
        yPosition,
        contentWidth,
        11,
        textColor,
        true
      );
      yPosition += awardNameHeight;
      const awardDetailHeight = addText(
        `${award.event} - ${award.year}`,
        margin + 4,
        yPosition,
        contentWidth,
        descriptionFontSize,
        accentColor,
        false
      );
      yPosition += awardDetailHeight;

      if (awardIndex < data.awards.length - 1) {
        drawLine(
          margin,
          yPosition,
          pageWidth - margin,
          yPosition,
          lightGray,
          0.3
        );
        yPosition += 5;
      }
    });
  }

  // ========== FOOTER ==========
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.setFont("helvetica", "normal");
    doc.text(
      `Page ${i} of ${totalPages}`,
      pageWidth - margin - 15,
      pageHeight - 8
    );
  }

  // Generate and download the PDF
  const name = `${
    data.basics.name
  } Over ${calculateYearsOfExperience()} Years Experience Backend Developer Resume`;
  const fileName = `${name.replace(/\s+/g, "-")}.pdf`;
  doc.save(fileName);
};
